import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import {
  ClipboardList, CheckCircle2, XCircle, MessageCircleQuestion,
  Clock, Award, Search, Filter, Star, Mail, ArrowRight, ArrowLeft,
  Download, User, Building2, Calendar, ChevronDown, ChevronUp, Trash2,
  RefreshCw, Eye, Send, ShieldCheck, AlertCircle,
} from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/date";
import api from "@/lib/api";
import { AdminModal } from "@/components/AdminModal";
import { Header } from "./Schools";

// One submission = one row, whichever collection it came from. feedback_submissions
// (standalone /feedback form) and registration_submissions (Teacher Registration's review
// step, tagged source:"registration" by the /feedback endpoint) use different field names
// for the same concepts — this is the single normalization point so the table itself stays
// source-agnostic. No values are invented: each falls back only to another already-existing
// field, never a fabricated default.
function normalizeSub(s) {
  const consents = [s.consent_info_correct, s.consent_certificate, s.consent_email];
  const consentPct = Math.round((consents.filter(Boolean).length / 3) * 100);
  return {
    name: s.participant_name || s.full_name || "",
    email: s.email || "",
    program: s.program_title || "",
    trainer: s.trainer_name || "",
    school: s.school || s.school_name || "",
    completion: s.source === "registration" ? consentPct : s.completion_percent,
    rating: s.overall_rating ?? s.review_rating ?? null,
  };
}

export function AdminFeedbackList() {
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState(null); // registration-sourced sub being viewed, or null
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [l, s, c] = await Promise.all([
        api.get("/feedback", { params: status === "all" ? { q } : { status, q } }),
        api.get("/feedback/stats"),
        api.get("/feedback/chart-data"),
      ]);
      // /feedback already returns teacher reviews captured during Teacher Registration
      // (tagged source: "registration") alongside the standalone feedback submissions —
      // one unified list, one table, no second section.
      setSubs(l.data || []);
      setStats(s.data);
      setChartData(c.data);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const cards = [
    { key: "total", label: "Total submitted", icon: ClipboardList, color: "bg-indigo-50 text-[#4F46E5] border-indigo-100" },
    { key: "today", label: "Today", icon: Calendar, color: "bg-cyan-50 text-cyan-700 border-cyan-100" },
    { key: "this_month", label: "This month", icon: Calendar, color: "bg-sky-50 text-sky-700 border-sky-100" },
    { key: "pending", label: "Pending review", icon: Clock, color: "bg-amber-50 text-amber-700 border-amber-100" },
    { key: "approved", label: "Approved", icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    { key: "rejected", label: "Rejected", icon: XCircle, color: "bg-rose-50 text-rose-700 border-rose-100" },
    { key: "certificates_issued", label: "Certificates issued", icon: Award, color: "bg-purple-50 text-purple-700 border-purple-100" },
    { key: "avg_rating", label: "Avg. rating", icon: Star, color: "bg-yellow-50 text-yellow-700 border-yellow-100", suffix: "/5" },
  ];

  return (
    <div className="fade-up">
      <Header
        title="Training Feedback"
        subtitle="Review submissions and issue certificates after approval."
      />

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-4" data-testid="feedback-stats">
        {cards.map((c) => {
          const val = stats?.[c.key];
          const Icon = c.icon;
          return (
            <div key={c.key} className={`rounded-2xl border p-4 ${c.color}`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className="w-4 h-4" />
                <span className="text-2xl font-bold">{val ?? "—"}{val != null && c.suffix ? c.suffix : ""}</span>
              </div>
              <div className="text-xs font-medium opacity-80">{c.label}</div>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      {chartData && (
        <div className="mt-6 grid lg:grid-cols-2 gap-5" data-testid="feedback-charts">
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="text-sm font-semibold text-gray-700 mb-3">Monthly Feedback Trend</div>
            {chartData.monthly_trend?.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData.monthly_trend} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#4F46E5" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-gray-400 py-10 text-center">No data yet</p>}
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="text-sm font-semibold text-gray-700 mb-3">Rating Distribution</div>
            {chartData.rating_distribution?.some(r => r.count > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData.rating_distribution} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="rating" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#D97706" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-gray-400 py-10 text-center">No ratings yet</p>}
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 lg:col-span-2">
            <div className="text-sm font-semibold text-gray-700 mb-3">Feedback by Program</div>
            {chartData.by_program?.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData.by_program} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="program" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0891B2" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-gray-400 py-10 text-center">No data yet</p>}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, program…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#4F46E5]"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", "pending", "approved", "rejected", "changes_requested"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`feedback-filter-${s}`}
              className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${
                status === s ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
              }`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-gray-500">
          {subs.length} {subs.length === 1 ? "submission" : "submissions"}
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                <th className="text-left px-4 py-3 font-semibold">Participant</th>
                <th className="text-left px-4 py-3 font-semibold">Program · Trainer</th>
                <th className="text-left px-4 py-3 font-semibold">School</th>
                <th className="text-left px-4 py-3 font-semibold">Complete</th>
                <th className="text-left px-4 py-3 font-semibold">Rating</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Cert / Email</th>
                <th className="text-right px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">Loading…</td></tr>
              )}
              {!loading && subs.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <div className="text-sm font-medium">No submissions yet.</div>
                </td></tr>
              )}
              {subs.map((s) => {
                const n = normalizeSub(s);
                const isReg = s.source === "registration";
                const openReview = () => (isReg ? setReviewModal(s) : nav(`/admin/feedback/${s.id}`));
                return (
                  <tr
                    key={s.id}
                    className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                    onClick={openReview}
                    data-testid={`feedback-row-${s.id}`}
                  >
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {fmtDate(s.submitted_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0F172A]">{n.name || n.email}</div>
                      {n.name && <div className="text-xs text-gray-500">{n.email}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[#0F172A]">{n.program || "—"}</div>
                      <div className="text-xs text-gray-500">{n.trainer || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{n.school || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="w-24 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${n.completion || 0}%` }} />
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{n.completion ?? 0}%</div>
                    </td>
                    <td className="px-4 py-3">
                      {n.rating ? (
                        <span className="inline-flex items-center gap-0.5 text-xs">
                          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {n.rating}/5
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={s.status} certId={s.certificate_id} /></td>
                    <td className="px-4 py-3">
                      <EmailStatusDot emailSent={s.certificate_email_sent} emailSentAt={s.certificate_email_sent_at} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); openReview(); }}
                        data-testid={`feedback-view-${s.id}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[#4F46E5] hover:underline"
                      >
                        Review <ArrowRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Complete response for a Teacher Registration review — the standalone feedback
          submissions instead use the existing full detail page at /admin/feedback/:id. */}
      {reviewModal && (
        <AdminModal
          onClose={() => setReviewModal(null)}
          title="Review Feedback"
          subtitle={fmtDate(reviewModal.submitted_at)}
          testId="reg-review-modal"
          maxWidth="max-w-lg"
        >
          <RegistrationReviewDetail r={reviewModal} />
        </AdminModal>
      )}
    </div>
  );
}

function RegistrationReviewDetail({ r }) {
  const n = normalizeSub(r);
  return (
    <div className="space-y-4" data-testid={`reg-review-${r.id}`}>
      <div>
        <div className="text-xs text-gray-500 mb-1">Participant</div>
        <div className="text-sm font-semibold text-[#0F172A]">{n.name || n.email}</div>
        {n.name && <div className="text-xs text-gray-500">{n.email}</div>}
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">Program</div>
        <div className="text-sm text-[#0F172A]">{n.program || "—"}{n.trainer ? ` · ${n.trainer}` : ""}</div>
      </div>
      {n.rating ? (
        <div>
          <div className="text-xs text-gray-500 mb-1">Overall Rating</div>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <span key={s} className={`text-xl ${s <= n.rating ? "text-amber-400" : "text-gray-200"}`}>★</span>
            ))}
            <span className="ml-2 text-sm text-gray-600 self-center">{n.rating}/5</span>
          </div>
        </div>
      ) : null}
      {r.review_experience && (
        <div>
          <div className="text-xs text-gray-500 mb-1">Overall Experience</div>
          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-[#EEF2FF] text-[#4F46E5]">
            {r.review_experience}
          </span>
        </div>
      )}
      {r.review_text && (
        <div>
          <div className="text-xs text-gray-500 mb-1">Feedback</div>
          <p className="text-sm text-[#0F172A] leading-relaxed italic">"{r.review_text}"</p>
        </div>
      )}
      {r.review_answers && Object.keys(r.review_answers).length > 0 && (
        <div className="space-y-3 pt-2 border-t border-gray-100">
          {Object.entries(r.review_answers).map(([qId, ans]) => (
            <div key={qId}>
              <div className="text-xs text-gray-500 mb-0.5">{qId}</div>
              <div className="text-sm text-[#0F172A]">
                {typeof ans === "number"
                  ? <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((s) => <span key={s} className={`text-base ${s <= ans ? "text-amber-400" : "text-gray-200"}`}>★</span>)}</div>
                  : Array.isArray(ans) ? ans.join(", ") : String(ans)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailStatusDot({ emailSent, emailSentAt }) {
  if (!emailSent) return <span className="text-[10px] text-gray-400">Not sent</span>;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold">
        <Mail className="w-3 h-3" /> Sent
      </span>
      {emailSentAt && (
        <span className="text-[10px] text-gray-400">
          {fmtDate(emailSentAt)}
        </span>
      )}
    </div>
  );
}

function StatusBadge({ status, certId }) {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    changes_requested: "bg-indigo-50 text-[#4F46E5] border-indigo-200",
  };
  const label = status?.replace("_", " ") || "pending";
  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${map[status] || map.pending}`}>
        {label}
      </span>
      {certId && (
        <span className="text-[10px] font-mono text-gray-500">{certId}</span>
      )}
    </div>
  );
}

export function AdminFeedbackDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [cert, setCert] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [changesNote, setChangesNote] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, qs] = await Promise.all([
        api.get(`/feedback/${id}`),
        api.get("/feedback/questions"),
      ]);
      setSub(s.data);
      setQuestions(qs.data);
      setAdminNotes(s.data.admin_notes || "");
      // Fetch cert details if available
      if (s.data.certificate_id) {
        try {
          const cr = await api.get(`/certificates`, { params: {} });
          const found = (cr.data || []).find(c => c.certificate_id === s.data.certificate_id);
          if (found) setCert(found);
        } catch {}
      }
    } catch {
      toast.error("Could not load submission");
      nav("/admin/feedback");
    }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    if (!window.confirm("Approve this feedback? A certificate will be generated and emailed to the participant.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/feedback/${id}/approve`, { admin_notes: adminNotes });
      toast.success(`Approved · Certificate ${data.certificate_id}${data.email_sent ? " · Email sent" : " · Email pending"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not approve");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error("Please add a rejection reason"); return; }
    setBusy(true);
    try {
      await api.post(`/feedback/${id}/reject`, { reason: rejectReason });
      toast.success("Rejected · participant notified");
      setRejectOpen(false);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject");
    } finally {
      setBusy(false);
    }
  };

  const requestChanges = async () => {
    setBusy(true);
    try {
      await api.post(`/feedback/${id}/request-changes`, { note: changesNote });
      toast.success("Change request sent");
      setChangesOpen(false);
      setChangesNote("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally {
      setBusy(false);
    }
  };

  const resendEmail = async () => {
    if (!window.confirm("Re-send the certificate email to the participant?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/feedback/${id}/resend-email`);
      toast.success(`Certificate email re-sent (resend #${data.resend_count})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-send email");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this feedback submission? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/feedback/${id}`);
      toast.success("Submission deleted");
      nav("/admin/feedback");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
      setBusy(false);
    }
  };

  if (!sub || !questions) return <div className="p-8 text-gray-500">Loading…</div>;

  const groups = [
    { label: "Training feedback", questions: questions.training, answers: sub.training_answers },
    { label: "Trainer feedback", questions: questions.trainer, answers: sub.trainer_answers },
    { label: "Suggestions", questions: questions.suggestions, answers: sub.suggestions },
  ];

  const canApprove = sub.status !== "approved";
  const canReject  = !["rejected"].includes(sub.status);
  const certDownloadUrl = cert?.download_token
    ? `${api.defaults.baseURL}/certificates/download/${cert.download_token}`
    : sub.certificate_id
      ? `${api.defaults.baseURL}/certificates/${sub.certificate_id}/pdf`
      : null;

  return (
    <div className="fade-up" data-testid="feedback-detail">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/admin/feedback" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#0F172A]">
          <ArrowLeft className="w-3.5 h-3.5" /> All submissions
        </Link>
      </div>

      <Header
        title={sub.participant_name}
        subtitle={
          <span className="text-sm text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-1">
            <StatusBadge status={sub.status} certId={sub.certificate_id} />
            <span>·</span>
            <span>{sub.program_title}</span>
            <span>·</span>
            <span>{sub.training_date || "—"}</span>
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {sub.certificate_id && certDownloadUrl && (
              <>
                <a
                  href={certDownloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:border-gray-400 text-[#0F172A] text-sm font-medium px-3.5 py-2 rounded-lg"
                  data-testid="feedback-cert-download"
                >
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </a>
                <a
                  href={`/verify/${sub.certificate_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:border-gray-400 text-[#0F172A] text-sm font-medium px-3.5 py-2 rounded-lg"
                  data-testid="feedback-cert-verify"
                >
                  <Eye className="w-3.5 h-3.5" /> Preview cert
                </a>
                <button
                  onClick={resendEmail}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 text-sm font-medium px-3.5 py-2 rounded-lg"
                  data-testid="feedback-resend-email"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Re-send email
                </button>
              </>
            )}
            {canReject && (
              <button
                onClick={() => setChangesOpen((v) => !v)}
                data-testid="feedback-request-changes-btn"
                className="inline-flex items-center gap-1.5 bg-white border border-indigo-200 text-[#4F46E5] hover:bg-indigo-50 text-sm font-medium px-3.5 py-2 rounded-lg"
              >
                <MessageCircleQuestion className="w-3.5 h-3.5" /> Request changes
              </button>
            )}
            {canReject && (
              <button
                onClick={() => setRejectOpen((v) => !v)}
                data-testid="feedback-reject-btn"
                className="inline-flex items-center gap-1.5 bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm font-medium px-3.5 py-2 rounded-lg"
              >
                <XCircle className="w-3.5 h-3.5" /> Reject
              </button>
            )}
            {canApprove && (
              <button
                onClick={approve}
                disabled={busy}
                data-testid="feedback-approve-btn"
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow"
              >
                <CheckCircle2 className="w-4 h-4" /> {busy ? "Working…" : "Approve & issue certificate"}
              </button>
            )}
            <button
              onClick={remove}
              disabled={busy}
              data-testid="feedback-delete-btn"
              className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-500 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 text-sm font-medium px-3.5 py-2 rounded-lg disabled:opacity-60"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        }
      />

      {/* Reject / Changes inline forms */}
      {rejectOpen && (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <div className="text-sm font-semibold text-rose-800 mb-2">Reason for rejection</div>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            data-testid="feedback-reject-reason"
            className="w-full px-3 py-2 rounded-lg border border-rose-200 bg-white text-sm min-h-20"
            placeholder="Explain why this feedback cannot be approved…"
          />
          <div className="mt-3 flex gap-2">
            <button onClick={reject} disabled={busy} data-testid="feedback-reject-confirm" className="text-sm bg-rose-600 hover:bg-rose-700 text-white font-semibold px-4 py-2 rounded-lg">Send rejection</button>
            <button onClick={() => setRejectOpen(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-lg">Cancel</button>
          </div>
        </div>
      )}
      {changesOpen && (
        <div className="mt-4 rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
          <div className="text-sm font-semibold text-[#4F46E5] mb-2">What should the participant revise?</div>
          <textarea
            value={changesNote}
            onChange={(e) => setChangesNote(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-indigo-200 bg-white text-sm min-h-20"
            placeholder="e.g. Please expand your suggestions section…"
          />
          <div className="mt-3 flex gap-2">
            <button onClick={requestChanges} disabled={busy} className="text-sm bg-[#4F46E5] hover:bg-[#4338CA] text-white font-semibold px-4 py-2 rounded-lg">Send</button>
            <button onClick={() => setChangesOpen(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-[1fr,320px] gap-6">
        {/* Left column: content */}
        <div className="space-y-5">
          <DetailCard icon={User} title="Participant details" testid="detail-participant">
            <MetaGrid rows={[
              ["Full name", sub.participant_name],
              ["Email", sub.email],
              ["Mobile", sub.mobile],
              ["Age", sub.age],
              ["Gender", sub.gender],
              ["Designation", sub.designation],
              ["Experience", sub.experience],
              ["Subjects", (sub.subjects || []).join(", ")],
              ["Classes", (sub.classes || []).join(", ")],
            ]} />
          </DetailCard>

          <DetailCard icon={Building2} title="Training details" testid="detail-training">
            <MetaGrid rows={[
              ["Program", sub.program_title],
              ["Date", sub.training_date],
              ["Duration", sub.duration_hours ? `${sub.duration_hours} hours` : "—"],
              ["Trainer", sub.trainer_name],
              ["School", sub.school],
              ["District", sub.district],
              ["Attendance ID", sub.attendance_id],
            ]} />
          </DetailCard>

          {/* ── Training Review (compact) ────────────────────────── */}
          <DetailCard icon={ClipboardList} title="Training Review" testid="detail-review">
            <div className="space-y-4">
              {/* Star rating */}
              {sub.overall_rating && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">Overall Rating</div>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map((s) => (
                      <span key={s} className={`text-xl ${s <= sub.overall_rating ? "text-amber-400" : "text-gray-200"}`}>★</span>
                    ))}
                    <span className="ml-2 text-sm text-gray-600 self-center">{sub.overall_rating}/5</span>
                  </div>
                </div>
              )}

              {/* Experience */}
              {(sub.suggestions?.review_experience || sub.review_experience) && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">Overall Experience</div>
                  <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-[#EEF2FF] text-[#4F46E5]">
                    {sub.suggestions?.review_experience || sub.review_experience}
                  </span>
                </div>
              )}

              {/* Written feedback */}
              {(sub.suggestions?.review_text || sub.review_text) && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">Feedback</div>
                  <p className="text-sm text-[#0F172A] leading-relaxed italic">"{sub.suggestions?.review_text || sub.review_text}"</p>
                </div>
              )}

              {/* Dynamic review answers (from configurable questions) */}
              {sub.review_answers && Object.keys(sub.review_answers).length > 0 && (
                <div className="space-y-3 pt-2 border-t border-gray-100">
                  {Object.entries(sub.review_answers).map(([qId, ans]) => (
                    <div key={qId}>
                      <div className="text-xs text-gray-500 mb-0.5">{qId}</div>
                      <div className="text-sm text-[#0F172A]">
                        {typeof ans === "number"
                          ? <div className="flex gap-0.5">{[1,2,3,4,5].map((s) => <span key={s} className={`text-base ${s <= ans ? "text-amber-400" : "text-gray-200"}`}>★</span>)}</div>
                          : Array.isArray(ans) ? ans.join(", ") : String(ans)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Submission date */}
              <div>
                <div className="text-xs text-gray-500 mb-1">Submitted</div>
                <div className="text-sm text-[#0F172A]">{fmtDate(sub.submitted_at)}</div>
              </div>
            </div>
          </DetailCard>
        </div>

        {/* Right column: sidebar */}
        <aside className="space-y-4">
          {/* Snapshot */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Snapshot</div>
            <div className="grid gap-3">
              <StatRow label="Overall rating" value={sub.overall_rating ? `${sub.overall_rating} / 5` : "—"} />
              <StatRow label="Completion" value={`${sub.completion_percent}%`} />
              <StatRow label="Submitted" value={fmtDate(sub.submitted_at)} />
              {sub.reviewed_at && <StatRow label="Reviewed" value={fmtDate(sub.reviewed_at)} />}
              {sub.reviewed_by && <StatRow label="By" value={sub.reviewed_by} />}
            </div>
          </div>

          {/* Approval info */}
          {sub.status === "approved" && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="text-xs uppercase tracking-wider text-emerald-800 font-semibold mb-3 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Approval details
              </div>
              <div className="grid gap-2">
                <StatRow label="Approved by" value={sub.approved_by || sub.reviewed_by || "—"} />
                <StatRow label="Approved at" value={fmtDate(sub.approved_at)} />
                <StatRow label="Certificate status" value={sub.certificate_generated ? "Generated" : "—"} />
                {sub.certificate_generated_at && (
                  <StatRow label="Generated at" value={fmtDate(sub.certificate_generated_at)} />
                )}
              </div>
            </div>
          )}

          {/* Email status */}
          {sub.certificate_id && (
            <div className={`rounded-2xl border p-5 ${sub.certificate_email_sent ? "border-blue-200 bg-blue-50" : "border-amber-200 bg-amber-50"}`}>
              <div className={`text-xs uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5 ${sub.certificate_email_sent ? "text-blue-800" : "text-amber-800"}`}>
                <Mail className="w-3.5 h-3.5" /> Email status
              </div>
              <div className="grid gap-2">
                <StatRow
                  label="Email sent"
                  value={sub.certificate_email_sent ? "Yes" : "Not yet"}
                />
                {sub.certificate_email_sent_at && (
                  <StatRow label="Sent at" value={fmtDate(sub.certificate_email_sent_at)} />
                )}
                {cert?.resend_count > 0 && (
                  <StatRow label="Resent" value={`${cert.resend_count}×`} />
                )}
                {cert?.download_count > 0 && (
                  <StatRow label="Downloads" value={`${cert.download_count}`} />
                )}
              </div>
              {sub.certificate_email_sent && (
                <button
                  onClick={resendEmail}
                  disabled={busy}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-blue-800 border border-blue-300 bg-white hover:bg-blue-100 py-2 px-3 rounded-lg disabled:opacity-60"
                >
                  <RefreshCw className="w-3 h-3" /> Re-send email
                </button>
              )}
            </div>
          )}

          {/* Certificate card */}
          {sub.certificate_id && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-emerald-800 font-semibold mb-2">
                <Award className="w-3.5 h-3.5" /> Certificate issued
              </div>
              <div className="font-mono text-xs text-emerald-800 mb-3">{sub.certificate_id}</div>
              <div className="flex flex-col gap-2">
                {certDownloadUrl && (
                  <a
                    href={certDownloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="sidebar-cert-download"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800 border border-emerald-300 bg-white hover:bg-emerald-100 py-2 px-3 rounded-lg"
                  >
                    <Download className="w-3 h-3" /> Download PDF
                  </a>
                )}
                <a
                  href={`/verify/${sub.certificate_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800 border border-emerald-300 bg-white hover:bg-emerald-100 py-2 px-3 rounded-lg"
                >
                  <Eye className="w-3 h-3" /> Verify page
                </a>
              </div>
            </div>
          )}

          {/* Admin notes */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Admin notes</div>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Private notes to attach on approval…"
              data-testid="feedback-admin-notes"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm min-h-24"
            />
            <div className="mt-2 text-[10px] text-gray-400">Saved when you approve.</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function DetailCard({ icon: Icon, title, children, testid }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden" data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] border border-indigo-100 grid place-items-center">
            <Icon className="w-4 h-4 text-[#4F46E5]" />
          </div>
          <div className="text-sm font-semibold text-[#0F172A]">{title}</div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

// Sanitize a display value: treat corrupted chars, "None", "null", "undefined",
// "NaN", whitespace-only, and the replacement character as blank.
const _clean = (v) => {
  if (v == null) return null;
  const s = String(v).replace(/\ufffd/g, "").trim();
  if (!s || s === "None" || s === "null" || s === "undefined" || s === "NaN") return null;
  return s;
};

function MetaGrid({ rows }) {
  return (
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-3 border-b border-gray-50 pb-1.5">
          <dt className="text-gray-500 text-xs">{k}</dt>
          <dd className="text-[#0F172A] text-right">{_clean(v) || "\u2014"}</dd>
        </div>
      ))}
    </dl>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <div className="text-gray-500 text-xs">{label}</div>
      <div className="text-[#0F172A] font-medium text-xs">{value}</div>
    </div>
  );
}

function AnswerRender({ question, value }) {
  if (value === undefined || value === null || value === "") return <span className="text-gray-400">—</span>;
  if (question.type === "likert") {
    return (
      <span className="inline-flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={`w-3.5 h-3.5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
        ))}
        <span className="ml-1 text-xs text-gray-500">{value}/5</span>
      </span>
    );
  }
  if (question.type === "multi") {
    return (
      <div className="flex flex-wrap gap-1">
        {(value || []).map((v) => (
          <span key={v} className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-xs">{v}</span>
        ))}
      </div>
    );
  }
  if (question.type === "yes_no") {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${
        value === "Yes" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"
      }`}>{value}</span>
    );
  }
  return <span>{value}</span>;
}

export default AdminFeedbackList;